<!--suppress HtmlUnknownTag -->
<vuejs3-todo>
    Ready
</vuejs3-todo>

<template>
    <section class="forgotten-password-complete-page">
        <h1 v-if="isExpired">{{ i18n.feedBack.expired }}</h1>
        <h1 v-else-if="isSuccessful">{{ i18n.feedBack.success }}</h1>
        <h1 v-else-if="error">{{ i18n._error }}</h1>
        <form @submit.prevent="submit"
              autocomplete="off"
              v-else
        >
            <input type="email"
                   disabled
                   :value="user.email"
            />
            <label>
                <span>{{ i18n.passwordLabel }}</span>
                <input type="password"
                       v-model="user.password"
                       required
                       :class="isPasswordInvalid ? 'invalid' : 'valid'"
                />
                <span :class="{strong: isPasswordInvalid}">
                    <template v-if="$i18n.lang === 'fr'">
                        Au moins 8 caractères, au moins un numéro (0-9) et au moins un caractère spécial (<code>-?!@#$%^*</code>)
                    </template>
                    <template v-else-if="$i18n.lang === 'de'">
                        Es muss aus mindestens acht Zeichen bestehen und wenigstens eine Zahl (0-9) und ein Sonderzeichen (<code>-?!@#$%^*</code>) enthalten.
                    </template>
                    <template v-else>
                        At least 8 characters, at least one number (0-9) and at least one special character (<code>-?!@#$%^*</code>)
                    </template>
                </span>
            </label>
            <label>
                <span>{{ i18n.confirmLabel }}</span>
                <input type="password"
                       v-model="confirmPassword"
                       required
                       :class="isPasswordConfirmed ? 'valid' : 'invalid'"
                />
                <span v-if="!isPasswordConfirmed"
                      class="strong"
                >{{ i18n.passwordNotConfirmed }}</span>
            </label>
            <div>
                <button type="submit"
                        :disabled="!canBeSubmitted"
                >{{ i18n.submit }}</button>
            </div>
        </form>
    </section>
</template>

<script>
    import axios from "@/inside/axios";
    import User from "@/common/models/User";

    const i18n = {
        de: {
            confirmLabel: "Geben Sie Ihr Passwort zur Bestätigung erneut ein",
            feedBack: {
                expired: "Ihre Bitte um Reset des Passwortes ist nicht mehr gültig!",
                success: "Ihr Passwort wurde zurückgesetzt!"
            },
            passwordLabel: "Wählen Sie ein Passwort",
            passwordNotConfirmed: "Die beiden Passwörter sind nicht identisch",
            submit: "Passwort zurücksetzen"
        },
        en: {
            confirmLabel: "Confirm your new password",
            feedBack: {
                expired: "Your password reset request is no longer valid!",
                success: "Your password has been reset!"
            },
            passwordLabel: "Enter your new password",
            passwordNotConfirmed: "The two passwords are not identical",
            submit: "Reset password"
        },
        fr: {
            confirmLabel: "Confirmez votre nouveau mot de passe",
            feedBack: {
                expired: "Votre demande de réinitialisation de mot de passe n'est plus valide !",
                success: "Votre mot de passe a été réinitialisé !"
            },
            passwordLabel: "Choisissez votre mot de passe",
            passwordNotConfirmed: "Les deux mots de passe ne correspondent pas",
            submit: "Réinitialiser le mot de passe"
        }
    };

    export default {
        computed: {
            /**
             * Returns a boolean indicating whether password reset can be submitted.
             * @return {boolean}
             */
            canBeSubmitted() {
                return !this.isPasswordInvalid && this.isPasswordConfirmed;
            },
            i18n() {
                return this.$i18n.strings(i18n, this.$options.name);
            },
            /**
             * Returns a boolean indicating whether passwords are matching.
             * @return {boolean}
             */
            isPasswordConfirmed() {
                return (
                    this.confirmPassword !== null
                    && this.confirmPassword.trim() === this.user.password
                );
            },
            /**
             * Returns a boolean indicating whether password respects validation rules.
             * @return {boolean}
             */
            isPasswordInvalid() {
                return !User.passwordRegExp.test(this.user.password);
            }
        },
        created() {
            const url = new URL(document.location.href);

            if (!url.searchParams.has("token")) {
                this.$router.replace("/login");
                return;
            }

            this.token = url.searchParams.get("token");

            if (this.token !== "test") {
                const data = JSON.parse(atob(this.token.split(".")[1]));
                this.user.email = data.uid;
            }
        },
        data() {
            return {
                confirmPassword: null,
                error: false,
                isExpired: false,
                isSuccessful: false,
                token: null,
                user: {
                    email: "sarah.connor@echoline.fr",
                    password: null
                }
            };
        },
        methods: {
            /**
             * Sends a password reset completion request to Echoline API.
             * @return {void}
             */
            submit() {
                if (!this.canBeSubmitted) {
                    return;
                }

                axios.post("/rpc", {
                    action: "customers.password.reset",
                    params: {
                        token: this.token,
                        password: this.user.password
                    }
                }, {
                    validateStatus: status => status === 200
                }).then(() => {
                    this.isSuccessful = true;

                    setTimeout(() => {
                        window.location.href = "/outside.html#/login";
                    }, 3000);
                }).catch((error) => {
                    if (
                        Object.prototype.hasOwnProperty.call(error, "response")
                        && error.response.resource === "error"
                        && error.response.data.code === "1"
                    ) {
                        this.expired = true;
                        return;
                    }

                    this.error = true;
                });
            }
        },
        name: "ForgottenPasswordCompletePage"
    }
</script>

<style lang="scss">
    @import "../../colors";
    @import "../../mixins";
    @import "../../variables";

    .forgotten-password-complete-page {
        display: flex;
        height: 100vh;
        width: 100vw;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        background-image: url("/img/outside_forgotten-password-request-page_background.svg");
        background-position: bottom right;
        background-repeat: no-repeat;
        background-size: 32%;

        form {
            width: 40%;
            background-color: white;
            box-shadow: 0 0 4px black;
            border-radius: 8px;
            box-sizing: border-box;
            padding: 32px 32px 0;

            input {
                font-family: inherit;
                font-size: inherit;
                line-height: 24px;
                border: 1px solid $color-border;
                padding: 7px 11px;
                border-radius: 4px;
                box-sizing: border-box;
                width: 100%;

                &:focus,
                &:active {
                    outline: none;
                    border-color: $color-blue-medium;
                }

                &.invalid {
                    border-color: $color-red-dark;
                }

                &.valid {
                    border-color: $color-green-dark;
                }
            }

            label {
                margin: 16px 0;
                display: block;

                span {
                    font-size: 12px;
                    margin: 0 4px;
                    line-height: 24px;

                    &:first-child,
                    &.strong {
                        font-weight: $font-weight-bold;
                    }
                }
            }



            div {
                display: flex;
                justify-content: right;
                line-height: 40px;
                margin: 32px 0;

                button {
                    line-height: 38px;
                    border: 1px solid $color-mint;
                    background-color: $color-mint;
                    color: white;
                    font-family: inherit;
                    font-size: inherit;
                    padding: 0 12px;
                    border-radius: 4px;
                    cursor: pointer;
                    @include transition(color, background-color);

                    &:hover {
                        color: $color-mint;
                        background-color: transparent;
                    }
                }
            }
        }

        h1 {
            font-weight: $font-weight-bold;
        }
    }
</style>
